import { z } from "zod";

export const navGroupSchema = z.enum(["data_assurance", "modules", "market", "insight", "setup"]);
export type NavGroup = z.infer<typeof navGroupSchema>;

/** Badge sources a navigation entry can show; values come with the /modules response. */
export const badgeSourceSchema = z.enum([
  "open_critical_findings",
  "installs_held",
  "held_submissions",
]);
export type BadgeSource = z.infer<typeof badgeSourceSchema>;

export const navEntrySchema = z.object({
  group: navGroupSchema,
  label: z.string().min(1),
  route: z.string().regex(/^\/[a-z0-9-]+$/),
  badge: badgeSourceSchema.optional(),
  tag: z.string().optional(),
});
export type NavEntry = z.infer<typeof navEntrySchema>;

export const moduleStateSchema = z.enum(["built", "preview", "catalogue"]);
export const tenantModuleStateSchema = z.enum([
  "active",
  "disabled",
  "preview",
  "retailer_tenants",
  "retailer_add_on",
  "separate_product",
]);

/** One module as the API presents it to a tenant (GET /modules). */
export const moduleSchema = z.object({
  key: z.string().min(1),
  name: z.string().min(1),
  state: moduleStateSchema,
  tenantState: tenantModuleStateSchema,
  description: z.string(),
  forBuyer: z.string(),
  requiresConnections: z.array(z.string()),
  nav: z.array(navEntrySchema),
});
export type Module = z.infer<typeof moduleSchema>;

export const modulesResponseSchema = z.object({
  items: z.array(moduleSchema),
  badges: z.record(badgeSourceSchema, z.number().int().nonnegative()),
});
export type ModulesResponse = z.infer<typeof modulesResponseSchema>;

/** config/modules.yaml, validated at start-up (MOD-01). */
export const moduleManifestSchema = z.object({
  key: z.string().regex(/^[a-z][a-z0-9-]*$/),
  name: z.string().min(1),
  state: moduleStateSchema,
  tenant_types: z.array(z.enum(["wholesaler", "retailer"])).min(1),
  rule_packs: z.array(z.string()),
  nav: z.array(navEntrySchema),
  permissions: z.array(z.string().regex(/^[a-z]+\.[a-z_]+$/)),
  events: z.array(z.string().regex(/^[A-Z][A-Za-z]+$/)),
  requires_connections: z.array(z.string()),
  flag: z.string().optional(),
  description: z.string().optional(),
  for_buyer: z.string().optional(),
});
export type ModuleManifest = z.infer<typeof moduleManifestSchema>;

export const modulesFileSchema = z.object({
  version: z.literal(1),
  nav_groups: z.array(navGroupSchema),
  modules: z.array(moduleManifestSchema).min(1),
});
export type ModulesFile = z.infer<typeof modulesFileSchema>;
